import React from "react"

import { DateComponent } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { updateField } from "../../slice"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"
import { IDateField } from "../../index"
import { DateTime } from "luxon"

export interface Props {
    field: IDateField
}

export const DateField = ({ field }: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const { isLoading } = useAppSelector((state: GlobalState) => state.fields)

    const {
        position: _position,
        odataType: _odataType,
        value,
        ...fieldProps
    } = field

    let deserializedValue: Date | undefined
    if (value) {
        // We need to trick a Javascript date into ignoring the difference between the user timezone and instead use the profile's one.
        const localDateTime = DateTime.fromISO(value)
        const systemDateTime = localDateTime.setZone("system", {
            keepLocalTime: true,
        })
        deserializedValue = systemDateTime.toJSDate()
    } else {
        deserializedValue = undefined
    }
    const {
        ClickHereToEdit,
        RequiredFieldErrorMessage,
        PleaseEnterAValidDateFormat,
    } = Labels

    return (
        <DateComponent
            {...fieldProps}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            value={deserializedValue}
            isLoading={isLoading}
            placeholder={ClickHereToEdit}
            mandatoryErrorText={RequiredFieldErrorMessage}
            validationErrorText={`${PleaseEnterAValidDateFormat} ${field.displayFormat}`}
            onSave={(changedValue: Date | null): void => {
                if (
                    changedValue?.toLocaleDateString() ===
                    deserializedValue?.toLocaleDateString()
                ) {
                    return
                }
                let newValue: string | null = null

                if (changedValue) {
                    if (value) {
                        const remoteDateTime = DateTime.fromISO(value)
                        const localDateTime = DateTime.fromJSDate(
                            changedValue,
                            { zone: "system" },
                        )
                        newValue = remoteDateTime
                            .set({
                                day: localDateTime.day,
                                month: localDateTime.month,
                                year: localDateTime.year,
                            })
                            .toISO()
                    } else {
                        newValue = changedValue.toISOString()
                    }
                }

                dispatch(
                    updateField({
                        field,
                        newValue,
                    }),
                )
            }}
        />
    )
}

export default DateField
